import { Box, Typography, Modal, ModalDialog, ModalClose } from "@mui/joy";
import { Trash2, AlertTriangle } from "lucide-react";
import { useState } from "react";

import { Button } from "../../../../../../shared";

interface ActionProps {
    onDelete: () => void;
    isDeleting?: boolean;
    productName?: string;
}

export function Action({ onDelete, isDeleting = false, productName }: ActionProps) {
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    function handleDeleteClick() {
        setShowDeleteDialog(true);
    }

    function handleDeleteConfirm() {
        setShowDeleteDialog(false);
        onDelete();
    }

    function handleDeleteCancel() {
        setShowDeleteDialog(false);
    }

    return (
        <Box mt={3}>
            <Box
                sx={{
                    border: "1px solid",
                    borderColor: "danger.outlinedBorder",
                    borderRadius: "4px",
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 2,
                        py: 1,
                        bgcolor: "danger.softBg",
                        color: "danger.softColor",
                        borderBottom: "1px solid",
                        borderColor: "danger.outlinedBorder",
                    }}
                >
                    <AlertTriangle size={16} />
                    <Typography level="title-sm" sx={{ color: "inherit" }}>
                        Danger Zone
                    </Typography>
                </Box>

                <Box sx={{ p: 2 }}>
                    <Typography level="title-sm" sx={{ mb: 0.5 }}>
                        Delete this inventory item
                    </Typography>
                    <Typography level="body-sm" color="neutral">
                        Removes this supplier's pricing for
                        {productName ? <strong> {productName}</strong> : " this product"}. Supplier comparisons and
                        estimates will stop pricing it from this supplier and fall back to whoever else carries it.
                    </Typography>
                    <Typography level="body-xs" color="neutral" sx={{ mt: 1 }}>
                        The product itself stays in the manufacturer catalog, so you can add the pricing back later.
                    </Typography>

                    <Box display="flex" justifyContent="flex-end" mt={2}>
                        <Button
                            variant="solid"
                            color="danger"
                            onClick={handleDeleteClick}
                            disabled={isDeleting}
                            startDecorator={<Trash2 size={16} />}
                        >
                            {isDeleting ? "Deleting..." : "Delete Inventory Item"}
                        </Button>
                    </Box>
                </Box>
            </Box>

            <Modal open={showDeleteDialog} onClose={handleDeleteCancel}>
                <ModalDialog variant="outlined" role="alertdialog" sx={{ maxWidth: 420, borderRadius: "4px" }}>
                    <ModalClose />
                    <Box display="flex" alignItems="center" gap={1} mb={1} sx={{ color: "danger.plainColor" }}>
                        <AlertTriangle size={20} />
                        <Typography level="title-md" sx={{ color: "inherit" }}>
                            Delete inventory item
                        </Typography>
                    </Box>
                    <Typography level="body-sm" color="neutral">
                        This permanently deletes this supplier's pricing for
                        {productName ? <strong> {productName}</strong> : " this product"}. This action cannot be
                        undone.
                    </Typography>

                    <Box display="flex" gap={2} justifyContent="flex-end" mt={3}>
                        <Button variant="soft" color="neutral" onClick={handleDeleteCancel} disabled={isDeleting}>
                            Cancel
                        </Button>
                        <Button
                            variant="solid"
                            color="danger"
                            onClick={handleDeleteConfirm}
                            disabled={isDeleting}
                            loading={isDeleting}
                            startDecorator={<Trash2 size={16} />}
                        >
                            Delete
                        </Button>
                    </Box>
                </ModalDialog>
            </Modal>
        </Box>
    );
}
